/**
 * useBeslutslage - Reusable Hook for Beslutsläge Premium Feature
 * 
 * Encapsulates all common premium logic:
 * - Stripe checkout creation
 * - Payment status polling
 * - Case management (save, load, reopen)
 * - Dev mode bypass
 * 
 * USAGE:
 * const { 
 *   isPremiumUnlocked, 
 *   isProcessing, 
 *   handleActivatePremium,
 *   ... 
 * } = useBeslutslage('avverkning', { inputs, result });
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  createBeslutslageCheckout, 
  pollCheckoutStatus,
  getBeslutslageCase,
  checkBeslutslageReturn,
  clearBeslutslageParams,
  refreshCaseStatus
} from './beslutslageApi';
import { getProductType, BESLUTSLAGE_PRICE } from './beslutslageConfig';
import { SALES_ENABLED, MAINTENANCE_MSG } from './featureFlags';

/**
 * @param {string} calculatorId - The calculator identifier (e.g., 'avverkning', 'gallring')
 * @param {object} options - { inputs, result, language }
 */
export const useBeslutslage = (calculatorId, options = {}) => {
  const { user } = useAuth();
  const isLoggedIn = user !== null;
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { language = 'sv' } = options;
  
  // Premium/Payment state
  const [isPremiumUnlocked, setIsPremiumUnlocked] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [currentCaseId, setCurrentCaseId] = useState(null);
  const [isLoadingCase, setIsLoadingCase] = useState(false);
  const [restoredData, setRestoredData] = useState(null);
  
  // Dev mode (ONLY on localhost - NEVER in production via URL params)
  // This prevents free premium access in production via ?dev=true
  const [devMode] = useState(
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1'
  );

  // Labels
  const labels = {
    sv: {
      checkingPayment: 'Vi kontrollerar betalningsstatus...',
      paymentVerifyTimeout: 'Kunde inte bekräfta betalningen. Försök ladda om sidan.',
      loadCaseError: 'Kunde inte ladda ärendet',
      checkoutError: 'Kunde inte starta betalningen'
    },
    de: {
      checkingPayment: 'Zahlungsstatus wird überprüft...',
      paymentVerifyTimeout: 'Zahlung konnte nicht bestätigt werden. Bitte Seite neu laden.',
      loadCaseError: 'Fall konnte nicht geladen werden',
      checkoutError: 'Zahlung konnte nicht gestartet werden'
    },
    en: {
      checkingPayment: 'Checking payment status...',
      paymentVerifyTimeout: 'Payment verification timed out. Please refresh.',
      loadCaseError: 'Could not load case',
      checkoutError: 'Could not start checkout'
    }
  };
  const t = labels[language] || labels.sv;

  // ==========================================
  // RETURN FROM STRIPE CHECKOUT
  // ==========================================
  useEffect(() => {
    const checkReturn = async () => {
      const returnInfo = checkBeslutslageReturn();
      
      if (returnInfo.success && returnInfo.sessionId && returnInfo.caseId) {
        setIsCheckingPayment(true);
        setCurrentCaseId(returnInfo.caseId);
        setPaymentCancelled(false);
        // Clear URL params immediately so a refresh / navigation does not
        // re-trigger the polling loop and leave the UI in a stuck state.
        clearBeslutslageParams();
        
        try {
          let attempts = 0;
          const maxAttempts = 10;
          
          const poll = async () => {
            const status = await pollCheckoutStatus(returnInfo.sessionId);
            
            if (status.payment_status === 'paid' && status.premium_unlocked) {
              // Payment successful - load the case
              const caseData = await getBeslutslageCase(returnInfo.caseId);
              
              // Restore calculation state
              if (caseData.calculation_data) {
                setRestoredData(caseData.calculation_data);
              }
              
              setIsPremiumUnlocked(true);
              setShowPaymentSuccess(true);
              setIsCheckingPayment(false);
              
              // Auto-hide success banner after 30 seconds
              setTimeout(() => setShowPaymentSuccess(false), 30000);
              return;
            }
            
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 2000);
            } else {
              setPaymentError(t.paymentVerifyTimeout);
              setIsCheckingPayment(false);
            }
          };
          
          await poll();
        } catch (error) {
          console.error('Payment check error:', error);
          setPaymentError(error.message);
          setIsCheckingPayment(false);
        }
      } else if (returnInfo.cancelled && returnInfo.caseId) {
        // User cancelled - show gentle message
        setPaymentCancelled(true);
        setCurrentCaseId(returnInfo.caseId);
        clearBeslutslageParams();
        
        // Try to load the case data to show results
        try {
          const caseData = await getBeslutslageCase(returnInfo.caseId);
          if (caseData.calculation_data) {
            setRestoredData(caseData.calculation_data);
          }
        } catch (e) {
          console.error('Could not load cancelled case:', e);
        }
      }
    };
    
    if (isLoggedIn) {
      checkReturn();
    }
  }, [isLoggedIn, t.paymentVerifyTimeout]);

  // ==========================================
  // OPEN CASE FROM URL (Mina ärenden)
  // ==========================================
  useEffect(() => {
    const openCaseId = searchParams.get('open_case');
    
    if (openCaseId && isLoggedIn && !isLoadingCase) {
      setIsLoadingCase(true);
      
      getBeslutslageCase(openCaseId)
        .then(caseData => {
          if (caseData.calculation_data) {
            setRestoredData(caseData.calculation_data);
          }
          
          setCurrentCaseId(openCaseId);
          setIsPremiumUnlocked(caseData.premium_unlocked);
          
          // Clear only open_case param
          searchParams.delete('open_case');
          if (!searchParams.get('calc')) {
            searchParams.set('calc', calculatorId);
          }
          setSearchParams(searchParams, { replace: true });
        })
        .catch(err => {
          console.error('Failed to open case:', err);
          setPaymentError(t.loadCaseError);
        })
        .finally(() => {
          setIsLoadingCase(false);
        });
    }
  }, [searchParams, isLoggedIn, isLoadingCase, setSearchParams, calculatorId, t.loadCaseError]);

  // ==========================================
  // ACTIVATE PREMIUM (STRIPE CHECKOUT)
  // ==========================================
  const handleActivatePremium = useCallback(async (calculationData) => {
    // Wartemodus: kaufpfad im öffentlichen UI deaktiviert.
    // Short-circuit before any auth redirect or network call.
    if (!SALES_ENABLED) {
      toast.info(MAINTENANCE_MSG);
      return;
    }

    if (!isLoggedIn) {
      const target = encodeURIComponent(`/verktyg?calc=${calculatorId}`);
      window.location.href = `/login?redirect=${target}&intent=purchase`;
      return;
    }
    
    setIsProcessingCheckout(true);
    setPaymentError(null);

    // Immediate visible feedback while we create the Stripe session.
    const toastId = toast.loading(
      language === 'de' ? 'Sichere Zahlung wird vorbereitet…'
      : language === 'en' ? 'Preparing secure payment…'
      : 'Förbereder säker betalning…'
    );
    
    try {
      const productType = getProductType(calculatorId);
      const checkout = await createBeslutslageCheckout(productType, calculationData);

      toast.success(
        language === 'de' ? 'Weiterleitung zu Stripe…'
        : language === 'en' ? 'Redirecting to Stripe…'
        : 'Skickar dig till Stripe…',
        { id: toastId }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = checkout.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      setPaymentError(error.message || t.checkoutError);
      setIsProcessingCheckout(false);
      toast.error(
        (error.message || t.checkoutError),
        { id: toastId }
      );
    }
  }, [isLoggedIn, calculatorId, t.checkoutError, language]);

  // ==========================================
  // DEV MODE UNLOCK (Testing only)
  // ==========================================
  const handleDevUnlock = useCallback(() => {
    setIsPremiumUnlocked(true);
  }, []);

  // ==========================================
  // RESET STATE (New calculation)
  // ==========================================
  const resetPremiumState = useCallback(() => {
    setIsPremiumUnlocked(false);
    setPaymentError(null);
    setPaymentCancelled(false);
    setCurrentCaseId(null);
    setRestoredData(null);
  }, []);

  // ==========================================
  // REFRESH STATUS
  // ==========================================
  const handleRefreshStatus = useCallback(async () => {
    if (!currentCaseId) return;
    
    try {
      const status = await refreshCaseStatus(currentCaseId);
      if (status.premium_unlocked) {
        setIsPremiumUnlocked(true);
      }
      return status;
    } catch (error) {
      console.error('Refresh status error:', error);
      return null;
    }
  }, [currentCaseId]);

  return {
    // State
    isPremiumUnlocked,
    isCheckingPayment,
    isProcessingCheckout,
    isLoadingCase,
    paymentError,
    paymentCancelled,
    showPaymentSuccess,
    setShowPaymentSuccess,
    currentCaseId,
    restoredData,
    devMode,
    isLoggedIn,
    
    // Actions
    handleActivatePremium,
    handleDevUnlock,
    resetPremiumState,
    handleRefreshStatus,
    
    // Config
    price: BESLUTSLAGE_PRICE,
    
    // Labels
    labels: t
  };
};

export default useBeslutslage;
